import { useState } from "react";
import { useNavigate } from "react-router-dom";

const platforms = [
  { icon: "ri-facebook-fill", name: "Facebook", color: "text-blue-600", stars: "2.1M+", bg: "bg-blue-50" },
  { icon: "ri-youtube-fill", name: "YouTube", color: "text-red-600", stars: "980K+", bg: "bg-red-50" },
  { icon: "ri-twitter-x-fill", name: "Twitter / X", color: "text-gray-800", stars: "760K+", bg: "bg-gray-50" },
  { icon: "ri-tiktok-fill", name: "TikTok", color: "text-gray-900", stars: "1.4M+", bg: "bg-pink-50" },
];

const features = [
  {
    icon: "ri-store-3-line",
    title: "Choose from Verified Factories",
    desc: "Browse 5 million+ products directly from Prime Verified Middle East factories and local warehouses. Pick what fits your audience.",
  },
  {
    icon: "ri-bar-chart-2-line",
    title: "Track Sales in Real Time",
    desc: "Your personal dashboard shows every sale, every click, and every order — live. Know exactly how your promotions are performing.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Commission Paid via PayPal",
    desc: "All commissions are automatically calculated and paid directly to your PayPal account. Fast, transparent, and hassle-free.",
  },
  {
    icon: "ri-global-line",
    title: "Sell to the World",
    desc: "Your followers are everywhere. Promote factory-direct products globally and earn commission on every sale — no inventory needed.",
  },
];

const mockStars = [
  { name: "Sarah K.", platform: "ri-youtube-fill", platformColor: "text-red-500", sold: "12,480", earned: "$3,744", avatar: "https://public.readdy.ai/ai/img_res/28ba8606a4e32fc1e1d2bbb0a4cc30cf.jpg" },
  { name: "Ahmed R.", platform: "ri-tiktok-fill", platformColor: "text-gray-800", sold: "9,210", earned: "$2,763", avatar: "https://readdy.ai/api/search-image?query=young%20male%20arab%20social%20media%20influencer%20smiling%20professional%20headshot%20clean%20white%20background%20portrait%20photography%20high%20quality&width=80&height=80&seq=star2&orientation=squarish" },
  { name: "Lina M.", platform: "ri-facebook-fill", platformColor: "text-blue-600", sold: "7,650", earned: "$2,295", avatar: "https://readdy.ai/api/search-image?query=young%20female%20middle%20eastern%20social%20media%20influencer%20smiling%20professional%20headshot%20clean%20white%20background%20portrait%20photography%20high%20quality&width=80&height=80&seq=star3&orientation=squarish" },
];

const DropshippingSection = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #b8860b 0%, #d4a017 25%, #f5c842 50%, #d4a017 75%, #b8860b 100%)" }}>
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #fff8dc, transparent)" }}></div>
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #fff8dc, transparent)" }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-5" style={{ background: "radial-gradient(circle, #ffffff, transparent)" }}></div>
        {/* Star sparkles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-60"
            style={{
              top: `${10 + (i * 7) % 80}%`,
              left: `${5 + (i * 13) % 90}%`,
              animation: `pulse ${1.5 + (i % 3) * 0.5}s ease-in-out infinite`,
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8 py-20">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2 mb-6">
            <i className="ri-star-fill text-white text-sm"></i>
            <span className="text-white text-xs font-semibold uppercase tracking-widest">Global Stars Program</span>
            <i className="ri-star-fill text-white text-sm"></i>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Dropship &amp; Global Stars Together
            <br />
            <span className="text-amber-900">Help Middle East Factories Sell to the World</span>
          </h2>
          <p className="text-white/90 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            Facebook, YouTube, Twitter &amp; TikTok stars unite with <strong>5 million Prime Verified Factories</strong> across the Middle East.
            Choose products, promote to your audience, track every sale, and earn commission — all paid via PayPal.
          </p>
        </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {platforms.map((p, i) => (
            <div key={i} className="bg-white/95 rounded-2xl p-5 text-center">
              <div className={`w-12 h-12 flex items-center justify-center ${p.bg} rounded-full mx-auto mb-3`}>
                <i className={`${p.icon} ${p.color} text-2xl`}></i>
              </div>
              <div className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{p.stars}</div>
              <div className="text-xs text-gray-500 font-medium">{p.name} Stars</div>
            </div>
          ))}
        </div>

        {/* Main Content: Features + Star Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">

          {/* Features */}
          <div className="space-y-3">
            <h3 className="text-white font-bold text-xl mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>How the Stars Program Works</h3>
            {features.map((f, i) => (
              <div
                key={i}
                onClick={() => setActiveFeature(i)}
                className={`rounded-xl p-5 cursor-pointer transition-all duration-200 ${activeFeature === i ? "bg-white" : "bg-white/20 hover:bg-white/30"}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${activeFeature === i ? "bg-amber-100" : "bg-white/20"}`}>
                    <i className={`${f.icon} text-lg ${activeFeature === i ? "text-amber-700" : "text-white"}`}></i>
                  </div>
                  <div>
                    <div className={`font-semibold text-sm mb-1 ${activeFeature === i ? "text-gray-900" : "text-white"}`}>{f.title}</div>
                    {activeFeature === i && (
                      <p className="text-gray-600 text-xs leading-relaxed">{f.desc}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Star Leaderboard + Commission Panel */}
          <div className="flex flex-col gap-5">
            <div className="bg-white/95 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Top Stars This Month</h3>
                <span className="text-xs text-amber-700 font-semibold bg-amber-50 px-3 py-1 rounded-full">Live Rankings</span>
              </div>
              <div className="space-y-4">
                {mockStars.map((star, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="text-amber-600 font-bold text-sm w-5 text-center">#{i + 1}</div>
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={star.avatar} alt={star.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 text-sm">{star.name}</span>
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${star.platform} ${star.platformColor} text-sm`}></i>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">{star.sold} units sold</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-amber-700">{star.earned}</div>
                      <div className="text-xs text-gray-400">via PayPal</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Commission Info */}
            <div className="bg-amber-900/80 backdrop-blur-sm rounded-2xl p-6 border border-amber-700/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-full">
                  <i className="ri-paypal-fill text-amber-300 text-xl"></i>
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">Commission via PayPal</div>
                  <div className="text-amber-300 text-xs">Automatic &amp; Transparent</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-amber-800/60 rounded-xl p-3">
                  <div className="text-amber-400 font-bold text-lg">5–15%</div>
                  <div className="text-amber-200 text-xs">Commission Rate</div>
                </div>
                <div className="bg-amber-800/60 rounded-xl p-3">
                  <div className="text-amber-400 font-bold text-lg">Weekly</div>
                  <div className="text-amber-200 text-xs">Payout Cycle</div>
                </div>
                <div className="bg-amber-800/60 rounded-xl p-3">
                  <div className="text-amber-400 font-bold text-lg">$0</div>
                  <div className="text-amber-200 text-xs">Joining Fee</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Factory Membership Pricing */}
        <div className="mb-14">
          <div className="bg-white/95 rounded-3xl p-8 md:p-10 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-amber-100 rounded-full px-4 py-2 mb-4">
                <i className="ri-building-2-line text-amber-700 text-sm"></i>
                <span className="text-amber-800 text-xs font-bold uppercase tracking-widest">Factory &amp; Local Warehouse Membership</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Start Free — Grow Without Risk
              </h3>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">
                Verified factories and local warehouses get full platform access to reach global stars and buyers worldwide.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Free Plan */}
              <div className="relative rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 p-7">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-amber-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">First 3 Months FREE</span>
                </div>
                <div className="text-center mb-6 pt-2">
                  <div className="text-5xl font-black text-amber-700 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>$0</div>
                  <div className="text-gray-500 text-sm">for the first 3 months</div>
                </div>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-amber-800 text-xs"></i>
                    </div>
                    Full verified factory listing
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-amber-800 text-xs"></i>
                    </div>
                    Connect with global stars &amp; influencers
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-amber-800 text-xs"></i>
                    </div>
                    Local warehouse drop shipping enabled
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-amber-800 text-xs"></i>
                    </div>
                    Real-time sales &amp; order tracking
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-amber-800 text-xs"></i>
                    </div>
                    No credit card required
                  </li>
                </ul>
              </div>

              {/* Paid Plan */}
              <div className="rounded-2xl border-2 border-gray-200 bg-white p-7">
                <div className="text-center mb-6">
                  <div className="text-5xl font-black text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    $5<span className="text-xl font-semibold text-gray-400">/mo</span>
                  </div>
                  <div className="text-gray-500 text-sm">after the free trial period</div>
                </div>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-gray-600 text-xs"></i>
                    </div>
                    Everything in the free plan
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-gray-600 text-xs"></i>
                    </div>
                    Priority placement in search results
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-gray-600 text-xs"></i>
                    </div>
                    Unlimited product listings
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-gray-600 text-xs"></i>
                    </div>
                    Advanced analytics &amp; reports
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-gray-600 text-xs"></i>
                    </div>
                    Dedicated account support
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-gray-400 text-xs">
                <i className="ri-shield-check-line mr-1 text-amber-600"></i>
                Cancel anytime. No hidden fees. Membership fee is only <strong className="text-gray-600">$5/month</strong> after your free 3-month trial.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center">
            <button
              onClick={() => navigate("/influencer")}
              className="bg-amber-900 hover:bg-amber-950 text-white font-bold px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-star-fill mr-2"></i>
              Join as a Global Star
            </button>
            <button
              onClick={() => navigate("/products")}
              className="bg-white hover:bg-amber-50 text-amber-900 font-bold px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm border-2 border-amber-900/20"
            >
              <i className="ri-store-3-line mr-2"></i>
              Browse Factory Products
            </button>
          </div>
          <p className="text-white/70 text-xs mt-4">No inventory. No upfront cost. Just promote &amp; earn.</p>
        </div>

      </div>
    </section>
  );
};

export default DropshippingSection;
